import { EventEmitter } from "@angular/core";
import { WorkerData, WorkerResponse, statKey } from "@models/websockets";
import { BehaviorSubject } from "rxjs";
import { Socket, io } from "socket.io-client";
import { reproject } from "src/app/core/reprojection";
import { TableConfiguration } from "./data-extraction.datasource";
import { TableConfiguration as TimeseriesTableConfiguration } from "./timeseries-extraction.datasource";
import { Feature } from "geojson";
import { formatDistance } from "date-fns";
import { saveAs } from 'file-saver';
import { Sort } from "@angular/material/sort";

const CONCURRENT_MESSAGES_SEND = 50;

export interface FeatureCounts {
  total: number;
  points: number;
  shapes: number;
}

export const statKeySort: { [key: string]: number } = {
  time: 1,
  unit: 2,
  identify: 3,
  min: 4,
  max: 5,
  mean: 6,
  median: 7
}

export interface TableFeatureRow {
  feature_id: string;
  [key: string]: string;
}

export interface ExportDataKey {
  layer_name: string;
  stat: statKey | 'unit' | 'time';
}

export interface TableLayer {
  layer_id: string;
  raster_url: string;
}

export function replaceStringValuesForExport(value: string) {
  value = value.replace(/::/g, '_');
  value = value.replace(/ -- /g, '_');
  value = value.replace(/ - /g, '_');
  value = value.replace(/-/g, '_');
  value = value.replace(/ /g, '_');
  return value;
}

export type ExtractionState = 'initializing' | 'calculating' | 'finished';

export interface TableFeature {
  feature_id: string;
  feature: Feature,
  properties: { [key: string]: string },
}

export class BaseExtractionDatasource<T extends TableConfiguration | TimeseriesTableConfiguration> {
  private _tableConfiguration?: T;
  public get tableConfiguration() { return this._tableConfiguration; }

  public setTableConfiguration(config: T) {
    this._tableConfiguration = config;
  }

  private _layers: TableLayer[] = [];
  public get layers() { return this._layers; }

  public addLayer(layer: TableLayer) {
    this._layers.push(layer);
  }

  private _features: TableFeature[] = [];
  public get features() { return this._features; }

  public addFeature(feature: TableFeature) {
    feature.feature = reproject(feature.feature);
    this._features.push(feature);
  }

  public extractionState = new EventEmitter<ExtractionState>();


  private _websocket!: Socket;

  private _queueScheduler = new BehaviorSubject<(number)>(0);
  private _startQueueProcessing = new EventEmitter();
  private _queueFull = false;
  private _messageQueue: WorkerData[] = [];

  /**
   *
   */
  constructor() {
    this._websocket = io({
      path: '/api/websocket/'
    });

    this._startQueueProcessing.subscribe(() => {
      this._queueScheduler.next(this._queueScheduler.value);
    });

    this._queueScheduler.subscribe(current => {
      if (current === CONCURRENT_MESSAGES_SEND) {
        this._queueFull = true;
      }

      if (current < CONCURRENT_MESSAGES_SEND && this._messageQueue.length > 0) {
        const nextMessage = this._messageQueue.shift();
        this._queueScheduler.next(current + 1);
        this._websocket.emit('extractData', nextMessage);
      }
    });

    this._websocket.on("extractResult", (workerResult: WorkerResponse) => {
      this._finishedDatapoints += 1;

      if (this._finishedDatapoints === this._totalDataPoints) {
        this.extractionState.emit('finished');
        this.markCalculationAsFinished();
      }

      this._queueScheduler.next(this._queueScheduler.value - 1);

      const [feature_id, layer_id] = workerResult.unique_id.split("::");

      let result;
      if (workerResult.error) {
        result = 'error';
      } else {
        result = this.getTransformedResultFromResponse(workerResult, layer_id);
      }

      const rowIndex = this.rows.findIndex(row => row.feature_id === feature_id);
      this.rows[rowIndex][`${layer_id}::${workerResult.stat}`] = result!;
    });
  }

  protected getTransformedResultFromResponse(response: WorkerResponse, layer_id: string) { }

  public destroy() {
    this._websocket?.disconnect();

    this._startQueueProcessing.unsubscribe();
    this._queueScheduler.unsubscribe();
  }

  public headers: string[] = [];
  public propertyHeaders: string[] = [];
  public rows: TableFeatureRow[] = [];
  public sortedRows: TableFeatureRow[] = [];

  public startDataExtraction() {
    this.extractionState.emit('initializing');

    this.layers.forEach(layer => {
      this.tableConfiguration?.feature_stats.forEach(stat => {
        this.headers.push(`${layer.layer_id}::${stat}`);
      });
    });

    this.features.forEach(feature => {
      Object.keys(feature.properties!).forEach(propertyKey => {
        // TODO: We currently filter out any properties that have no name set, we could try to handle those better
        if (!this.propertyHeaders.includes(propertyKey) && propertyKey !== "") {
          this.propertyHeaders.push(propertyKey);
        }
      });

      this.rows.push({ feature_id: feature.feature_id, ...feature.properties });
    });

    this._websocket.on('worker_initialized', () => {
      this.startTime = new Date();

      this.startDataLoading();
      this.extractionState.emit('calculating');

      this.sortedRows = this.rows;
    });

    this._websocket.emit('initialize');
  }

  public sortData(sort: Sort) {
    const data = this.rows.slice();

    if (!sort.active || sort.direction === '') {
      this.sortedRows = data;
      return;
    }

    this.sortedRows = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      return a[sort.active].localeCompare(b[sort.active], undefined, { numeric: true }) * (isAsc ? 1 : -1);
    });
  }

  public getFeatureStats(feature: Feature) {
    const selectedStats = this.tableConfiguration?.feature_stats!;
    if (feature.geometry.type === "Point") {
      return selectedStats.filter(stat => stat === 'identify');
    } else {
      return selectedStats.filter(stat => stat !== 'identify');
    }
  }


  public startDataLoading() {
    for (let layer of this.layers) {
      for (let feature of this.features) {
        const featureStats = this.getFeatureStats(feature.feature);

        for (let stat of featureStats) {
          const message: WorkerData = {
            unique_id: `${feature.feature_id}::${layer.layer_id}`,
            raster: layer.raster_url,
            stat: stat,
            feature: feature.feature
          }

          this._totalDataPoints += 1;

          this._messageQueue.push(message);

          const rowIndex = this.rows.findIndex(row => row.feature_id === feature.feature_id);
          this.rows[rowIndex][`${layer.layer_id}::${stat}`] = 'loading';

          if (!this._queueFull) {
            this._startQueueProcessing.emit();
          }
        }
      }
    }
  }

  // #region Progress
  public startTime?: Date;
  public endTime?: Date;

  public finalCalculationTime: string = '';

  public markCalculationAsFinished() {
    this.endTime = new Date();
    this.finalCalculationTime = formatDistance(this.startTime!, this.endTime!, { includeSeconds: true });
  }

  private _totalDataPoints: number = 0;
  private _finishedDatapoints: number = 0;

  public get totalDataPoints() {
    return this._totalDataPoints ?? Infinity;
  }

  public get finishedDatapoints() {
    return this._finishedDatapoints;
  }

  public get progress() {
    return (this._finishedDatapoints / (this.totalDataPoints)) * 100;
  }

  public get bufferProgress() {
    return ((this._finishedDatapoints + this._queueScheduler.value) / (this.totalDataPoints)) * 100;
  }
  //#endregion

  // #region Export
  // protected createStringFromKeyObject(keyObject: ExportDataKey) { }
  protected createStringFromKeyObject(keyObject: ExportDataKey) {
    return replaceStringValuesForExport(`${keyObject.layer_name}::${keyObject.stat}`);
    // return replaceStringValuesForExport(`${this._tableConfiguration!.layer_name}::${format(keyObject.layer_time, this._tableConfiguration!.date_format)}::${keyObject.stat}`);
  }

  protected async startCsvDownload(csvString: string, filename: string) {
    const blob = new Blob([
      String.fromCharCode(0xFEFF),
      csvString
    ], { type: "text/csv;charset=utf-8" });

    saveAs(blob, filename);
  }
  //#endregion
}
